import React, {Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {Block, Button, Card, Col, Icon, LabelValue, LabelValueList, Menu, MenuDivider, MenuItem, Row, Text, Title} from '@mc/ui-v4';
import {EditorEvent, FeatureId, FeaturePointer, GroupFeaturePointer, LayerId} from '@mc/react-mapio';
import {SidebarContent} from './SidebarContent';
import {useLayerAttributes} from '../../hooks/useLayerAttributes';
import {
    FeatureDetailVO,
    FeatureUserActionDetailVO,
    LayerAttributeVO,
    LayerDetailVO,
    LayerVO,
    RightsApi,
    UserRightsVO,
    TopicVO,
    DocumentVO,
    FeatureImageVO,
    EnumTypLayerAttr,
    EnumInterpretace,
} from '../../api';
import {WaitForQueryData} from '@mc/react-app-v4';
import {useLayerDetail} from '../../hooks/useLayerDetail';
import {useFeatureDetail} from '../../hooks/useFeatureDetail';
import {mcClose, mcErase, mcMenuKebab, mcMoveLeft, mcSelect} from '@mc/react-ui-icons';
import {UseQueryResult, useQuery} from 'react-query';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import classNames from 'classnames';
import {useAppConfig} from '@mc/react-app-base';
import './FeatureDetailSidebar.scss';
import {CACHE_KEY_USER_LAYER_OPRAVNENI} from '../../queryKeys';
import {ImageGallery} from '../image/ImageGallery';
import {downloadDocument, getFeatureImageUrl} from '../../api/urls';
import {truncate, findNestedLabelById} from '../../helpers/DataMutators';
import {useIsMobile} from '../../hooks/useIsMobile';
import {BottomSheet} from '../mobile/BottomSheet';
import {DocumentTable, DocumentRow} from '../forms/document/DocumentTable';
import {useMapioContext} from '../../mapioContext';
import {DataLogTable} from '../tables/DataLogTable';
import {isUserDbLayer} from '../../helpers/LayerConfigUtils';
import {formatInterpretaceValue} from '../../helpers/interpretace';
import {sanitizeFeatureDetailHtml} from '../../helpers/sanitizeHtml';
import {ACCESSIBILITY_MAP, parseAccessibilityValue, renderAccessibilityIcons} from '../../assets/accessibility/accessibilityCodelist';
import {layerColorToCss} from '../search-panel/searchPanelTypes';
import {FeatureDetailPrintApi, FeatureDetailPrintButton, FeatureDetailPrintMenuItem, useFeatureDetailPrint} from '../print/FeatureDetailPrint';

type Props = {
    collapsedFeatureDetail: FeatureId;
    featureId: FeatureId;
    handleFeatureEditorEvent: (event: EditorEvent) => Promise<void>;
    layerId: LayerId;
    handleUpdate: (feature: any, layerAttributes: LayerAttributeVO[], initialValues: any) => void;
    onClose: () => void;
    selectedDetailFeature: GroupFeaturePointer[];
    setCollapsedFeatureDetail: Dispatch<SetStateAction<FeatureId>>;
    topic: TopicVO;
    /** Callback pro navigaci zpět do panelu hledání (zobrazí "Zpět na seznam") */
    onBackToList?: () => void;
};

export const defaultUpdateAction = 'Editovat objekt';

// Vícehodnotový číselník = každá hodnota jako samostatný štítek. Detail objektu z databázové vrstvy
// se skládá na serveru (šablona details/user_layer.ftl), tato varianta se použije jen pro detail
// složený až na klientovi z properties – styly proto držíme shodné se šablonou.
const CISELNIK_VALUES_STYLE: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
};

const CISELNIK_VALUE_STYLE: React.CSSProperties = {
    padding: '0 7px',
    border: '1px solid #dce0e8',
    borderRadius: 10,
    overflowWrap: 'anywhere',
};

const splitPraha1Header = (headerHtml: string): {headHtml: string; restHtml: string} => {
    const processed = sanitizeFeatureDetailHtml(renderAccessibilityIcons(headerHtml));
    const doc = new DOMParser().parseFromString(processed, 'text/html');
    const head = doc.querySelector('.praha1-detail-header-sticky');
    const rest = doc.querySelector('.praha1-detail-header-rest');
    if (!head && !rest) {
        return {headHtml: '', restHtml: processed};
    }
    return {headHtml: head?.outerHTML ?? '', restHtml: rest?.outerHTML ?? ''};
};

export const FeatureDetailSidebar: React.FC<Props> = ({
    collapsedFeatureDetail,
    featureId,
    handleFeatureEditorEvent,
    layerId,
    handleUpdate,
    onClose,
    selectedDetailFeature,
    setCollapsedFeatureDetail,
    topic,
    onBackToList,
}) => {
    //@ts-ignore
    const {serverContextPath, serverPublic} = useAppConfig();
    const isMobile = useIsMobile();
    // Pokud výběr obsahuje pouze vrstvy z externích zdrojů, tak není třeba volat dotazy na detaily
    const [shouldQuery, setShouldQuery] = useState(true);
    // featury z externích vrstev máme odděleně, protože se jejich properties berou z mapioContextu
    const [selectedExternalFeatureDetails, setSelectedExternalFeatureDetails] = useState([]);

    const mapioContext = useMapioContext();
    const {setSelectedDetailFeature} = mapioContext;
    const {externalLayers, externalLayersCatalog, mapRef} = mapioContext.value;

    const getSelectedExternalFeatures = useCallback(() => {
        const featuresProperties = [];
        if (!externalLayers || externalLayers.length === 0) return;
        selectedDetailFeature.forEach(detailFeature => {
            const selectedLayerId = detailFeature.layerId;
            const selectedFeatureId = detailFeature.featureId;
            if (!selectedLayerId || !selectedFeatureId) return;
            externalLayers.forEach(layer => {
                // Dle typů to má být jedině pole, ale u mě to zatím byl vždy objekt, tak raději nechávám variantu pro obojí
                if (Array.isArray(layer.data)) {
                    layer.data.forEach(l => {
                        l.features.forEach(feature => {
                            if (
                                feature.id?.toString() === selectedFeatureId?.toString() &&
                                (feature.properties.layerId === selectedLayerId || feature.properties.Layer === selectedLayerId || layer.id === selectedLayerId)
                            ) {
                                featuresProperties.push({...feature.properties, featureId: feature.id, layerId: selectedLayerId});
                            }
                        });
                    });
                } else {
                    // @ts-ignore
                    layer.data.features.forEach(feature => {
                        if (
                            feature.id?.toString() === selectedFeatureId?.toString() &&
                            (feature.properties.layerId === selectedLayerId || feature.properties.Layer === selectedLayerId || layer.id === selectedLayerId)
                        ) {
                            featuresProperties.push({...feature.properties, featureId: feature.id, layerId: selectedLayerId});
                        }
                    });
                }
            });
        });
        return featuresProperties;
    }, [externalLayers, selectedDetailFeature]);

    useEffect(() => {
        let selectedFeature = [];
        if (selectedDetailFeature.length) {
            selectedFeature = getSelectedExternalFeatures() ?? [];
        }
        setSelectedExternalFeatureDetails(selectedFeature);
        if (selectedFeature.length > 0) {
            setCollapsedFeatureDetail(selectedFeature[0].featureId);
        }
    }, [getSelectedExternalFeatures, selectedDetailFeature]);

    useEffect(() => {
        // Nastaví podmínku pro volání dotazů pokud vybrané featury jsou pouze externí
        if (selectedExternalFeatureDetails.length === selectedDetailFeature.length) {
            setShouldQuery(false);
        } else {
            setShouldQuery(true);
        }
    }, [selectedExternalFeatureDetails.length, selectedDetailFeature.length]);

    const featureDetaiQuery: UseQueryResult<FeatureDetailVO[]> = useFeatureDetail(
        selectedDetailFeature,
        externalLayers.map(item => item.id),
    );
    const isDataFeatureDetail: boolean = React.useMemo(() => Boolean(featureDetaiQuery.data?.length), [featureDetaiQuery.data]);
    // Právě jeden vybraný objekt → v mobilním sheetu ho zobrazíme jako plný detail bez rámečku karty,
    // s názvem objektu v hlavičce a křížkem vedle (místo generického „Vybrané objekty"). Při více objektech
    // zůstává seznam sbalených karet.
    const isSingleFeatureDetail: boolean = React.useMemo(
        () => isDataFeatureDetail && featureDetaiQuery.data?.length === 1,
        [isDataFeatureDetail, featureDetaiQuery.data],
    );
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    const [layerIds, setLayerIds] = useState([]);
    const [isMaximized, setIsMaximized] = useState(isMobile && serverPublic);
    // Tisk detailu objektu (MCW-29120) – veškerá logika tisku je v components/print.
    const detailPrint: FeatureDetailPrintApi = useFeatureDetailPrint();

    let getLayerId = null;
    let getFeatureId = null;

    if (!featureDetaiQuery.isFetching && !isDataFeatureDetail) {
        getLayerId = isUserDbLayer(layersConfigQuery.data, layerId) ? layerId.toString() : null;
        getFeatureId = isUserDbLayer(layersConfigQuery.data, layerId) ? featureId : null;
    }

    const layerAttributesQuery: UseQueryResult<LayerAttributeVO[]> = useLayerAttributes(getLayerId);
    const layerDetailQuery: UseQueryResult<LayerDetailVO> = useLayerDetail(getLayerId, getFeatureId);

    // Layer detail query má smysl pouze pro scénář "single layer detail".
    // Pro data detail (featureDetaiQuery) tyto query nechceme, aby blokovaly render.
    const shouldUseLayerDetailQueries = Boolean(getLayerId && getFeatureId) && !isDataFeatureDetail;

    const maskInactiveQuery = useCallback(<T,>(query: UseQueryResult<T>, enabled: boolean): UseQueryResult<T> => {
        if (enabled) {
            return query;
        }
        // Pokud query aktuálně nepoužíváme, zamaskujeme pouze stavové příznaky.
        // Důvod: WaitForQueryData rozhoduje podle isError/isLoading/isFetching napříč všemi query.
        // Bez maskování by stará chyba z předchozího výběru mohla zůstat "sticky"
        // a blokovat vykreslení i po úspěšném načtení relevantních dat.
        return {
            ...query,
            isError: false,
            isLoading: false,
            isFetching: false,
        } as UseQueryResult<T>;
    }, []);

    const waitLayerDetailQuery = useMemo(
        () => maskInactiveQuery(layerDetailQuery, shouldUseLayerDetailQueries),
        [layerDetailQuery, shouldUseLayerDetailQueries, maskInactiveQuery],
    );

    const waitLayerAttributesQuery = useMemo(
        () => maskInactiveQuery(layerAttributesQuery, shouldUseLayerDetailQueries),
        [layerAttributesQuery, shouldUseLayerDetailQueries, maskInactiveQuery],
    );

    useEffect((): void => {
        if (isDataFeatureDetail && !featureDetaiQuery.data.some((item): boolean => item.featureId === collapsedFeatureDetail)) {
            if (collapsedFeatureDetail === undefined || !featureDetaiQuery.data.some(fd => fd.featureId === collapsedFeatureDetail)) {
                setCollapsedFeatureDetail(featureDetaiQuery.data[0].featureId);
            }
            if (selectedDetailFeature.length > 1) {
                handleHighlightFeature({layerId: featureDetaiQuery.data[0].layerId, featureId: featureDetaiQuery.data[0].featureId});
            }
        }
        if (featureDetaiQuery.data) {
            setLayerIds(Array.from(Object.values(featureDetaiQuery.data), item => item.layerId));
        }
    }, [featureDetaiQuery.data]);

    const useOpravneniQuery = useQuery<UserRightsVO[]>(
        [CACHE_KEY_USER_LAYER_OPRAVNENI, layerIds],
        async () => (await RightsApi.getAllLayerRightsForLoggedInUser(layerIds)).data,
        {enabled: !!layerIds.length && !mapioContext.value.isIframe && !serverPublic},
    );

    const handleHighlightFeature = useCallback((featurePointer?: FeaturePointer): void => {
        if (mapRef.current.highlightFeature) {
            mapRef.current.highlightFeature(featurePointer, false);
        }
    }, []);

    const handleHoverOverFeature = useCallback((feautrePointer?: FeaturePointer) => {
        if (mapRef.current.highlightFeature) {
            mapRef.current.highlightFeature(feautrePointer, true);
        }
    }, []);

    //Test pro to aby když kliknu např na odkaz v detailu se mi detail nezabalil
    const isInteractiveTarget = (event: React.MouseEvent<HTMLElement>): boolean => {
        const target = event.target as HTMLElement | null;
        return Boolean(target?.closest?.('a, button, input, textarea, select, [role="button"], [onclick]'));
    };

    const handleCardOnClick = useCallback(
        (featurePointer: FeaturePointer, event: React.MouseEvent<HTMLElement>): void => {
            if (isInteractiveTarget(event)) {
                return;
            }
            if (collapsedFeatureDetail === featurePointer.featureId) {
                setCollapsedFeatureDetail(undefined);
                handleHighlightFeature();
            } else {
                setCollapsedFeatureDetail(featurePointer.featureId);
                handleHighlightFeature(featurePointer);
            }
        },
        [collapsedFeatureDetail],
    );

    const showCancelSelection = useMemo(() => {
        // je to zvláštní, opírat se o tyto atributy, ale přijde nám zbytečné kvůli tomu přidávat nový
        return topic == null || topic.showBackgroundSelection || topic.showForegroundSelection || topic.showSearch;
    }, [topic]);

    const handleEraseSelection = useCallback((): void => {
        mapRef.current.selectFeatures([]);
        setSelectedDetailFeature([]);
    }, []);

    /** Pokud je hodnota atributu příliž dlouhá, ořízneme ji a zárověň pokud je detail rozšířen, tak se
     *  zobrazí v plné délce */
    useEffect(() => {
        if (isDataFeatureDetail && !isMobile) {
            const elements = document.getElementsByClassName('ui-v4-label-value-list-item-value');
            for (const element of Array.from(elements)) {
                const htmlElement = element as HTMLElement;
                // Buňky s vlastním obsahem (ikony přístupnosti, obrázky, odkazy, formátovaný text,
                // štítky vícehodnotového číselníku) nezkracujeme – přepis na innerText by jejich HTML zahodil.
                if (htmlElement.querySelector('.accessibility-value, img, a, .formatted-text-value, .ciselnik-values')) {
                    continue;
                }
                // V tisku detailu chceme hodnoty vždy v plné délce (MCW-29120).
                if (htmlElement.closest('.feature-detail-print')) {
                    continue;
                }
                const value = htmlElement.innerText;
                const originalValue = htmlElement.dataset.originalValue; // Získání původního textu

                if (typeof value === 'string' && !isMaximized) {
                    // Uložení původního textu do data-atributu
                    if (!originalValue) {
                        htmlElement.dataset.originalValue = value;
                    }
                    htmlElement.innerHTML = truncate(value, 150);
                } else if (originalValue) {
                    // Obnova původního textu, pokud je isMaximized true
                    htmlElement.innerHTML = originalValue;
                }
            }
        }
    }, [featureDetaiQuery, collapsedFeatureDetail, isMaximized]);

    /** Název objektu – z detailu, jinak z vybrané featury v mapě. */
    const getFeatureLabel = useCallback(
        (item: FeatureDetailVO): string =>
            item?.label ||
            selectedDetailFeature.find(f => f.featureId?.toString() === item.featureId?.toString() || f.groupId?.toString() === item.featureId?.toString())
                ?.label ||
            'Nevyplněno',
        [selectedDetailFeature],
    );

    const cardTitle = (item: FeatureDetailVO, layersConfig: LayerVO): ReactNode => {
        const imageUrl = layersConfig?.layerImage ? layersConfig?.layerImage : layersConfig?.style?.marker;
        // Barva vrstvy shodně se zbytkem aplikace (LegendModal, filtr): style.color, jinak iconColor.
        // Předáváme do serverové hlavičkové šablony přes CSS proměnnou (název vrstvy obarvený barvou vrstvy).
        const layerColor = layerColorToCss(layersConfig?.style?.color ?? layersConfig?.iconColor);
        const itemLabel = getFeatureLabel(item);
        return (
            <>
                {item.headerHtml ? (
                    // HTML si tvoříme my na serveru ze šablony, tzn. důvěryhodnou kostru nesanitizujeme celou
                    // (odstranili bychom např. onclick na našich buttonech pro akce v návazných aplikacích).
                    // Sanitizují se pouze uzly s uživatelským formátovaným textem (.formatted-text-value).
                    // renderAccessibilityIcons nahradí marker .accessibility-value SVG ikonami přístupnosti.
                    <div
                        style={layerColor ? ({'--praha1-layer-color': layerColor} as React.CSSProperties) : undefined}
                        className="px-2 praha1-header"
                        dangerouslySetInnerHTML={{__html: sanitizeFeatureDetailHtml(renderAccessibilityIcons(item.headerHtml))}}
                    />
                ) : (
                    <Row className={'cursor-pointer px-2 py-1'} noGutters>
                        <Col className={'align-items-center d-flex pr-2'} xs={'auto'}>
                            {imageUrl && <img className={'icon'} src={serverContextPath + '/api/files/img/' + imageUrl} alt={layersConfig?.label} />}
                        </Col>
                        <Col>
                            <Title type={'block'}>{itemLabel}</Title>
                            <LabelValue className={'mb-0 font-weight-normal'} intent={'secondary'} title={layersConfig?.label}>
                                {truncate(layersConfig?.label, 40)}
                            </LabelValue>
                        </Col>
                    </Row>
                )}
            </>
        );
    };

    /** Ikona tiskárny vedle názvu objektu – ve veřejné (zjednodušené) verzi. */
    const printButton = (item: FeatureDetailVO, layersConfig: LayerVO): ReactNode => (
        <FeatureDetailPrintButton
            api={detailPrint}
            className={'mr-1 print-button'}
            item={item}
            layersConfig={layersConfig}
            style={'link'}
            title={getFeatureLabel(item)}
        />
    );

    /**
     * Seznam atributů detailu objektu z databázové vrstvy (varianta bez serverové šablony).
     * Vykresluje se v kartě detailu i v tisku detailu (MCW-29120), proto je vyčleněn samostatně.
     */
    const layerDetailAttributeList = (layerAttributes: LayerAttributeVO[], layerDetail: LayerDetailVO): ReactNode => (
        <LabelValueList>
            {layerAttributes.map((attribute: LayerAttributeVO) => {
                const key: string = attribute.nazev;
                const label: string = attribute.popis;
                const value = layerDetail.properties[key];

                if (attribute.typ === EnumTypLayerAttr.ACCESSIBILITY) {
                    const codes = parseAccessibilityValue(value as string);
                    return (
                        <LabelValue key={key} label={label}>
                            <div className="accessibility-display">
                                {codes.map(kod => {
                                    const item = ACCESSIBILITY_MAP.get(kod);
                                    if (!item) return null;
                                    return <img key={kod} src={item.icon} alt={item.nazev} title={item.nazev} className="accessibility-display__icon" />;
                                })}
                            </div>
                        </LabelValue>
                    );
                } else if (typeof value === 'boolean') {
                    return (
                        <LabelValue key={key} label={label}>
                            {value ? 'Ano' : 'Ne'}
                        </LabelValue>
                    );
                } else if (Array.isArray(value)) {
                    // číselníkové atributy jsou pole hodnot (LayerAttributeEnumVO[]); u vícehodnotového
                    // číselníku vypisujeme každou hodnotu jako samostatný štítek (stejně jako user_layer.ftl)
                    const nazvy: string[] = value.filter(v => v && v.nazev).map(v => v.nazev);
                    return (
                        <LabelValue key={key} label={label}>
                            {attribute.interpretace === EnumInterpretace.MULTI_CISELNIK ? (
                                <div className={'ciselnik-values'} style={CISELNIK_VALUES_STYLE}>
                                    {nazvy.map((nazev, idx) => (
                                        <span key={`${nazev}-${idx}`} className={'ciselnik-value'} style={CISELNIK_VALUE_STYLE}>
                                            {nazev}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                nazvy.join(', ')
                            )}
                        </LabelValue>
                    );
                } else if (value?.nazev) {
                    return (
                        <LabelValue key={key} label={label}>
                            {value.nazev}
                        </LabelValue>
                    );
                } else {
                    return (
                        <LabelValue key={key} label={label}>
                            {attribute.interpretace ? formatInterpretaceValue(value, attribute.interpretace, isMobile) : value}
                        </LabelValue>
                    );
                }
            })}
        </LabelValueList>
    );

    const maximizedFeatureDetail = useCallback(
        (item: FeatureDetailVO): ReactNode => {
            const imagesIds: number[] = item.images?.map(({id}) => id);
            const documents: DocumentRow<number>[] = item.documents?.map((doc: DocumentVO) => ({
                id: doc.id!,
                popis: doc.popis,
                nazev: doc.nazev,
                kodTypDokument: doc.typDokumentu?.nazev,
                url: doc.url,
            }));
            return (
                <div className={'ui-v4-block pt-1 overflow-y-auto'}>
                    <div className={'ui-v4-block-body'}>
                        {item.headerHtml ? (
                            <>
                                {' '}
                                <ImageGallery
                                    getThumbnailUrl={id => {
                                        const image = item.images.find((img: FeatureImageVO) => img.id === id);
                                        if (image?.thumbUrl) {
                                            return image.thumbUrl;
                                        }
                                        return getFeatureImageUrl(item, id, serverContextPath);
                                    }}
                                    getImageUrl={id => getFeatureImageUrl(item, id, serverContextPath)}
                                    imageSize={isMaximized ? 127 : 64}
                                    imageIds={imagesIds}
                                />
                                <div dangerouslySetInnerHTML={{__html: sanitizeFeatureDetailHtml(renderAccessibilityIcons(item.html))}} />
                            </>
                        ) : (
                            <>
                                <div dangerouslySetInnerHTML={{__html: sanitizeFeatureDetailHtml(renderAccessibilityIcons(item.html))}} />
                                {imagesIds?.length > 0 && (
                                    <>
                                        {isMaximized && <Text type={'detail2'}>Fotografie</Text>}
                                        <ImageGallery
                                            getThumbnailUrl={id => {
                                                const image = item.images.find((img: FeatureImageVO) => img.id === id);
                                                if (image?.thumbUrl) {
                                                    return image.thumbUrl;
                                                }
                                                return getFeatureImageUrl(item, id, serverContextPath);
                                            }}
                                            getImageUrl={id => getFeatureImageUrl(item, id, serverContextPath)}
                                            imageSize={isMaximized ? 127 : 64}
                                            imageIds={imagesIds}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {isMaximized && (
                            <>
                                {item.documents?.length > 0 && (
                                    <>
                                        <Text type={'detail2'}>Dokumenty</Text>
                                        <DocumentTable
                                            getDocumentUrl={id => {
                                                return `${serverContextPath}${downloadDocument(item.featureId, id)}`;
                                            }}
                                            documents={documents}
                                            editable={false}
                                        />
                                    </>
                                )}
                                {item.logs && item.logs.length > 0 && (
                                    <>
                                        <Text type={'detail2'}>Historie</Text>
                                        <DataLogTable featureId={item.featureId} datalog={item.logs} layerId={item.layerId} />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            );
        },
        [isMaximized],
    );

    const content = new Map();
    return (
        <>
            {isMobile && serverPublic ? (
                <BottomSheet
                    open
                    onClose={() => {
                        handleEraseSelection();
                        onClose();
                    }}
                    snapPoints={[14, 50, 92]}
                    defaultSnapPointIndex={1}
                    className={'feature-detail-sheet'}
                    hideClose={isSingleFeatureDetail}
                    title={
                        isSingleFeatureDetail ? undefined : (
                            <>
                                <Icon className={'mr-1'} icon={mcSelect} />
                                {'Vybrané objekty'}
                            </>
                        )
                    }
                    handleBackgroundColor="#F5F7FA"
                >
                    <WaitForQueryData queries={[layersConfigQuery, waitLayerDetailQuery, featureDetaiQuery]} fullscreen>
                        {() => {
                            if (!featureDetaiQuery.data?.length && !layerDetailQuery.data) {
                                return (
                                    <Card
                                        className={`sidepanel-card ui-v4-card ui-v4-card-mode-view collapsed`}
                                        extra={<Button className={'mr-1'} icon={<Icon icon={mcClose} />} onClick={handleEraseSelection} style={'link'} />}
                                        mode={'view'}
                                    >
                                        <Block>Nenalezena žádná data...</Block>
                                    </Card>
                                );
                            }

                            // Jeden objekt → plný detail bez rámečku karty; název objektu si vykreslí
                            // hlavička detailu (cardTitle) a křížek plave vpravo nahoře vedle názvu.
                            if (isSingleFeatureDetail) {
                                const item: FeatureDetailVO = featureDetaiQuery.data[0];
                                const layersConfig: LayerVO = layersConfigQuery.data?.[item.layerId];
                                return (
                                    <div className={'feature-detail-sheet-single b-unset'}>
                                        {/* Tisk detailu objektu (MCW-29120) – vlevo od křížku, tzn. také vedle názvu objektu. */}
                                        <FeatureDetailPrintButton
                                            api={detailPrint}
                                            className={'feature-detail-sheet-print'}
                                            iconSize={'lg'}
                                            item={item}
                                            layersConfig={layersConfig}
                                            title={getFeatureLabel(item)}
                                        />
                                        <Button
                                            className={'feature-detail-sheet-close'}
                                            icon={<Icon icon={mcClose} size={'lg'} />}
                                            onClick={(): void => {
                                                handleEraseSelection();
                                                onClose();
                                            }}
                                            aria-label={'Zavřít'}
                                        />
                                        {(() => {
                                            const layerColor = layerColorToCss(layersConfig?.style?.color ?? layersConfig?.iconColor);
                                            const colorStyle = layerColor ? ({'--praha1-layer-color': layerColor} as React.CSSProperties) : undefined;
                                            const {headHtml, restHtml} = item.headerHtml ? splitPraha1Header(item.headerHtml) : {headHtml: '', restHtml: ''};
                                            return (
                                                <>
                                                    <div className={'feature-detail-sheet-single-head'}>
                                                        {item.headerHtml
                                                            ? headHtml && (
                                                                  <div
                                                                      className={'px-2 praha1-header'}
                                                                      style={colorStyle}
                                                                      dangerouslySetInnerHTML={{__html: headHtml}}
                                                                  />
                                                              )
                                                            : cardTitle(item, layersConfig)}
                                                    </div>
                                                    <div className={'feature-detail-sheet-single-body'}>
                                                        {restHtml && (
                                                            <div
                                                                className={'px-2 praha1-header'}
                                                                style={colorStyle}
                                                                dangerouslySetInnerHTML={{__html: restHtml}}
                                                            />
                                                        )}
                                                        {maximizedFeatureDetail(item)}
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                );
                            }

                            if (isDataFeatureDetail) {
                                featureDetaiQuery.data.forEach((item: FeatureDetailVO): void => {
                                    const layersConfig: LayerVO = layersConfigQuery.data?.[item.layerId];
                                    const isCollapsedFeatureDetail: boolean = item.featureId === collapsedFeatureDetail;

                                    content.set(
                                        item.featureId,
                                        <Card
                                            className={`sidepanel-card ui-v4-card ui-v4-card-mode-view mb-1 ${isCollapsedFeatureDetail ? 'collapsed' : ''} `}
                                            extra={printButton(item, layersConfig)}
                                            key={item.featureId}
                                            mode={'view'}
                                            onClick={(event): void => {
                                                handleCardOnClick({featureId: item.featureId, layerId: item.layerId}, event);
                                            }}
                                            title={cardTitle(item, layersConfig)}
                                        >
                                            {isCollapsedFeatureDetail && maximizedFeatureDetail(item)}
                                        </Card>,
                                    );
                                });
                            }
                            return <div className="px-1 pr-2 overflow-y-auto b-unset h-100">{Array.from(content.values())}</div>;
                        }}
                    </WaitForQueryData>
                </BottomSheet>
            ) : (
                <SidebarContent
                    className={'feature-detail-sidebar'}
                    icon={mcSelect}
                    onClose={onClose}
                    title={'Vybrané objekty' + (selectedDetailFeature.length ? ' (' + selectedDetailFeature.length + ')' : '')}
                    setIsMaximized={setIsMaximized}
                    isMaximized={isMaximized}
                    isFeatureDetail
                >
                    <div className={classNames({'scrollable-content': !isMobile})}>
                        {onBackToList && (
                            <div className={'search-panel-back-btn px-2'}>
                                <Button
                                    style={'link'}
                                    icon={<Icon icon={mcMoveLeft} size={'sm'} />}
                                    onClick={() => {
                                        (onBackToList(), handleEraseSelection());
                                    }}
                                >
                                    Zpět na seznam
                                </Button>
                            </div>
                        )}
                        {shouldQuery && (
                            <WaitForQueryData queries={[waitLayerAttributesQuery, layersConfigQuery, waitLayerDetailQuery, featureDetaiQuery]}>
                                {() => {
                                    if (!featureDetaiQuery.data?.length && (!layerAttributesQuery.data?.length || !layerDetailQuery.data)) {
                                        return (
                                            <Card className={'mb-2 mx-2'} mode={'view'}>
                                                <Block>Nenalezena žádná data...</Block>
                                            </Card>
                                        );
                                    }

                                    const content = new Map();

                                    if (isDataFeatureDetail) {
                                        featureDetaiQuery.data.forEach((item): void => {
                                            const layersConfig: LayerVO = layersConfigQuery.data?.[item.layerId];
                                            const isCollapsedFeatureDetail: boolean = item.featureId === collapsedFeatureDetail;
                                            const opravneniData: UserRightsVO[] = useOpravneniQuery?.data;
                                            let userActionsAllowed: string[] = [];

                                            item.userActions?.map((action): void => {
                                                // akce jsou povoleny všechny ty, co nám pošle server - ten rozhoduje dle práv
                                                userActionsAllowed.push(action.label);
                                            });

                                            // Uživatelské akce zobrazujeme dle práv, tisk detailu je dostupný vždy (MCW-29120).
                                            const showUserActions: boolean = Boolean(
                                                !mapioContext.value.isIframe &&
                                                    opravneniData &&
                                                    item.userActions?.length &&
                                                    (userActionsAllowed.length ||
                                                        opravneniData[0]?.layers[0]?.actionRights?.allActions ||
                                                        opravneniData[0]?.layers[0]?.vlastnikVrstvy),
                                            );

                                            content.set(
                                                item.featureId,
                                                <Card
                                                    className={classNames('sidepanel-card mb-1 mx-2', {['collapsed']: isCollapsedFeatureDetail})}
                                                    extra={
                                                        // Ve veřejné verzi je tisk samostatnou ikonou vedle názvu objektu,
                                                        // v interní verzi je poslední položkou menu pod „tři tečky".
                                                        serverPublic ? (
                                                            printButton(item, layersConfig)
                                                        ) : (
                                                            <Button
                                                                className={'mr-1'}
                                                                icon={<Icon icon={mcMenuKebab} />}
                                                                menu={
                                                                    <Menu>
                                                                        {showUserActions &&
                                                                            item.userActions?.map(
                                                                                (action: FeatureUserActionDetailVO): ReactNode => (
                                                                                    <MenuItem
                                                                                        key={action.akceId}
                                                                                        onClick={async (): Promise<void> => {
                                                                                            await handleFeatureEditorEvent(action as EditorEvent);
                                                                                        }}
                                                                                        text={action.label}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                        {showUserActions && <MenuDivider />}
                                                                        <FeatureDetailPrintMenuItem
                                                                            api={detailPrint}
                                                                            item={item}
                                                                            layersConfig={layersConfig}
                                                                            title={getFeatureLabel(item)}
                                                                        />
                                                                    </Menu>
                                                                }
                                                                popupProps={{position: 'bottom right'}}
                                                                style={'link'}
                                                            />
                                                        )
                                                    }
                                                    // @ts-ignore
                                                    onMouseEnter={(): void => {
                                                        if (!isCollapsedFeatureDetail) {
                                                            handleHoverOverFeature({layerId: item.layerId, featureId: item.featureId});
                                                        }
                                                    }}
                                                    onMouseLeave={(): void => {
                                                        if (!isCollapsedFeatureDetail) {
                                                            handleHoverOverFeature();
                                                        }
                                                    }}
                                                    key={item.featureId}
                                                    mode={'view'}
                                                    onClick={(event): void => {
                                                        handleCardOnClick({featureId: item.featureId, layerId: item.layerId}, event);
                                                    }}
                                                    title={cardTitle(item, layersConfig)}
                                                >
                                                    {isCollapsedFeatureDetail && maximizedFeatureDetail(item)}
                                                </Card>,
                                            );
                                        });
                                    } else {
                                        const layerAttributes: LayerAttributeVO[] = layerAttributesQuery.data;
                                        const layerDetail: LayerDetailVO = layerDetailQuery.data;
                                        const layersConfig: LayerVO = layersConfigQuery.data?.[layerDetail?.properties['layerId']];
                                        layerDetail &&
                                            content.set(
                                                layerDetail.id,
                                                <Card
                                                    className={'sidepanel-card mb-2 mx-2'}
                                                    extra={
                                                        <Button
                                                            className={'mr-1'}
                                                            icon={<Icon icon={mcMenuKebab} />}
                                                            menu={
                                                                <Menu>
                                                                    <MenuItem
                                                                        onClick={() => handleUpdate(layerDetail, layerAttributes, layerDetail.properties)}
                                                                        text={defaultUpdateAction}
                                                                    />
                                                                    {/* Tisk je v menu poslední a je oddělený linkou (MCW-29120). */}
                                                                    <MenuDivider />
                                                                    <FeatureDetailPrintMenuItem
                                                                        api={detailPrint}
                                                                        title={`${layersConfig?.label ?? ''} ${layerDetail.id}`.trim()}
                                                                    >
                                                                        {layerDetailAttributeList(layerAttributes, layerDetail)}
                                                                    </FeatureDetailPrintMenuItem>
                                                                </Menu>
                                                            }
                                                            popupProps={{position: 'bottom right'}}
                                                            style={'link'}
                                                        />
                                                    }
                                                    mode={'view'}
                                                    title={
                                                        <Row className={'cursor-pointer px-2 py-1'} noGutters>
                                                            <Col className={'align-items-center d-flex pr-1'} xs={'auto'}>
                                                                <img
                                                                    className={'icon'}
                                                                    src={serverContextPath + '/api/files/img/' + layersConfig?.style?.marker}
                                                                    alt={layersConfig.label}
                                                                />
                                                            </Col>
                                                            <Col>
                                                                <Title type={'block'}>{layerDetail.id}</Title>
                                                                <Text intent={'secondary'} size={'normal'}>
                                                                    {layersConfig?.label}
                                                                </Text>
                                                            </Col>
                                                        </Row>
                                                    }
                                                >
                                                    <Block className={'pt-1'}>{layerDetailAttributeList(layerAttributes, layerDetail)}</Block>
                                                </Card>,
                                            );
                                    }
                                    return <div className={`${isMobile ? 'mobile-sidebar-h' : ''}`}>{Array.from(content.values())}</div>;
                                }}
                            </WaitForQueryData>
                        )}
                        {selectedExternalFeatureDetails.length > 0 && (
                            <>
                                {selectedExternalFeatureDetails.map(featureDetail => (
                                    <Card
                                        key={featureDetail.featureId}
                                        className={'sidepanel-card mb-2 mx-2'}
                                        mode={'view'}
                                        onClick={(event): void => {
                                            handleCardOnClick({featureId: featureDetail.featureId, layerId: featureDetail.layerId}, event);
                                        }}
                                        title={cardTitle({label: featureDetail.featureId}, {
                                            label: findNestedLabelById(externalLayersCatalog, featureDetail.layerId),
                                        } as LayerVO)}
                                    >
                                        {featureDetail.featureId === collapsedFeatureDetail && (
                                            <Block>
                                                <LabelValueList>
                                                    {Object.entries(featureDetail)
                                                        .filter(([key]) => !['layerId', 'featureId', 'Layer'].includes(key))
                                                        .map(([key, value]) => (
                                                            <LabelValue key={key} label={key}>
                                                                {typeof value === 'object' ? JSON.stringify(value) : value}
                                                            </LabelValue>
                                                        ))}
                                                </LabelValueList>
                                            </Block>
                                        )}
                                    </Card>
                                ))}
                            </>
                        )}
                    </div>
                    {showCancelSelection && (
                        <div className={'d-flex justify-content-end pr-2 py-1'}>
                            <Button icon={<Icon icon={mcErase} />} onClick={handleEraseSelection} style={'link'}>
                                Zrušit výběr objektů
                            </Button>
                        </div>
                    )}
                </SidebarContent>
            )}
            {/* Tiskový obsah detailu objektu (MCW-29120) – montuje se jen po dobu tisku. */}
            {detailPrint.node}
        </>
    );
};
